<template>
  <main class="route-strategy-page">
    <el-alert
      v-if="!strategy.enabled"
      title="路由策略当前已关闭，高阶模型配额和降级功能不会生效"
      type="warning"
      :closable="false"
      show-icon
      class="strategy-alert"
    />

    <!-- 第一行：路由策略编排 + 策略命中盘口 24h -->
    <section class="grid two">
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">路由策略编排</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">核心配置</span>
          </div>
          <el-switch v-model="strategy.enabled" active-text="启用" inactive-text="关闭" @change="toggleEnabled" />
        </div>

        <div class="router-map">
          <div class="route-box">
            <h4>请求入口</h4>
            <div class="route-item"><span>API Key 调用</span><span class="tag green">已配置</span></div>
          </div>
          <div class="arrow">→</div>
          <div class="route-box">
            <h4>策略引擎</h4>
            <div class="route-item"><span>每日上限</span><b>{{ strategy.enabled ? strategy.defaultDailyLimit + ' 次' : '未启用' }}</b></div>
            <div class="route-item"><span>超出降级</span><b v-if="strategy.degradationTarget">{{ strategy.degradationTarget }}</b><b v-else class="muted-text">未配置</b></div>
            <div class="route-item"><span>特殊额度覆盖</span><b v-if="strategy.accountOverrides.length">{{ strategy.accountOverrides.length }} 个账号</b><b v-else class="muted-text">未配置</b></div>
            <div class="route-item"><span>模型路由</span><b v-if="strategy.modelRoutes.length">{{ strategy.modelRoutes.length }} 条转换</b><b v-else class="muted-text">未配置</b></div>
          </div>
          <div class="arrow">→</div>
          <div class="route-box">
            <h4>模型池</h4>
            <div class="route-item" v-for="m in strategy.models.slice(0, 3)" :key="m"><span>{{ m }}</span><b class="tag green">高优</b></div>
            <div class="route-item" v-if="strategy.models.length > 3"><span>...</span><b class="tag">+{{ strategy.models.length - 3 }} 个</b></div>
            <div class="route-item" v-if="strategy.degradationTarget"><span>{{ strategy.degradationTarget }}</span><b class="tag amber">降级目标</b></div>
            <div class="route-item" v-if="!strategy.models.length && !strategy.degradationTarget"><span class="muted-text">暂无模型</span></div>
          </div>
        </div>
      </div>

      <!-- 策略命中盘口 -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">策略命中盘口</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">24h</span>
          </div>
        </div>
        <div class="depth" v-if="strategyHits.length">
          <div class="depth-row" v-for="h in strategyHits" :key="h.label">
            <b>{{ h.label }}</b>
            <div class="depth-bar"><span :style="{ width: h.pct + '%' }"></span></div>
            <strong>{{ h.count }}</strong>
          </div>
        </div>
        <div v-else class="panel-empty">暂无命中数据</div>
      </div>
    </section>

    <!-- 第二行：高阶模型使用限制 + 特殊账号配置 -->
    <section class="grid two" style="margin-top: 0">
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">高阶模型使用限制</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">配额</span>
          </div>
        </div>
        <el-form label-width="120px" size="small" class="strategy-form">
          <el-form-item label="默认每日上限">
            <el-input
              v-model.number="strategy.defaultDailyLimit"
              type="number"
              :min="1"
              :max="99999"
              :disabled="!strategy.enabled"
            />
          </el-form-item>
          <el-form-item label="降级目标模型">
            <el-select
              v-model="strategy.degradationTarget"
              filterable clearable
              placeholder="选择降级目标"
              :disabled="!strategy.enabled"
                          >
              <el-option
                v-for="m in availableModels.filter(x => !strategy.models.includes(x))"
                :key="m" :label="m" :value="m"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="高阶模型列表">
            <div class="model-tag-list">
              <el-tag v-for="(m, i) in strategy.models" :key="i"
                closable :disable-transitions="false" @close="removeModel(i)">{{ m }}</el-tag>
              <el-select
                v-model="modelToAdd" size="mini" filterable
                placeholder="选择模型"
                :disabled="!strategy.enabled" @change="onModelSelected"
                              >
                <el-option
                  v-for="m in availableModels.filter(x => !strategy.models.includes(x))" :key="m" :label="m" :value="m"
                />
              </el-select>
            </div>
          </el-form-item>
        </el-form>
        <div class="panel-footer">
          <el-button type="primary" size="mini" circle icon="el-icon-check" :loading="saving" @click="saveStrategy" title="保存策略" />
        </div>
      </div>

      <!-- 特殊账号配置 -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">特殊账号配置</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">额度覆盖</span>
          </div>
          <div class="panel-head-right">
            <el-input
              v-model="accountSearchQuery"
              size="mini"
              placeholder="搜索账号..."
              clearable
              class="account-search-input"
              @input="onAccountSearch"
            />
            <el-button size="small" type="primary" plain icon="el-icon-plus"
              :disabled="!strategy.enabled" @click="onAddAccount">添加</el-button>
          </div>
        </div>
        <el-table :data="pagedOverrides" size="small" style="width: 100%"
          v-if="filteredOverrides.length" max-height="200" class="account-table">
          <el-table-column label="账号" prop="accountName" min-width="100" />
          <el-table-column label="每日上限" prop="dailyLimit" width="90" />
          <el-table-column label="操作" width="80">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="onEditAccount(scope.row)">编辑</el-button>
              <el-button size="mini" type="text" class="delete-btn" @click="onDeleteAccount(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-if="filteredOverrides.length > 0"
          class="account-pagination"
          layout="total, prev, pager, next"
          small
          :total="filteredOverrides.length"
          :page-size="accountPageSize"
          :current-page.sync="accountPage"
          @current-change="accountPage = $event"
        />
        <!-- 移动端卡片 -->
        <div class="account-cards" v-if="filteredOverrides.length">
          <div class="account-card" v-for="(a, i) in pagedOverrides" :key="i">
            <div class="account-card__row"><span class="label">账号</span><span>{{ a.accountName }}</span></div>
            <div class="account-card__row"><span class="label">每日上限</span><span>{{ a.dailyLimit }}</span></div>
            <div class="account-card__row">
              <span class="label">操作</span>
              <span>
                <el-button size="mini" type="text" @click="onEditAccount(a)">编辑</el-button>
                <el-button size="mini" type="text" class="delete-btn" @click="onDeleteAccount(i)">删除</el-button>
              </span>
            </div>
          </div>
        </div>
        <el-empty v-else :description="accountSearchQuery ? '无匹配账号' : '暂无特殊账号'" :image-size="60" />
      </div>
    </section>

    <!-- 模型路由：请求最开始的模型转换规则 -->
    <section class="grid two" style="margin-top: 0">
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">模型路由</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">模型转换</span>
          </div>
          <el-switch v-model="strategy.modelRoutingEnabled" active-text="启用" inactive-text="关闭" @change="toggleModelRouting" />
        </div>
        <p class="form-help" style="margin: 0 0 10px;">
          请求最开始时将用户指定的模型转换为目标模型；多组规则按顺序匹配，只生效一轮，不叠加。
        </p>
        <div class="route-rule-list" v-if="strategy.modelRoutes.length">
          <div class="route-rule-row" v-for="(r, i) in strategy.modelRoutes" :key="i">
            <el-select
              v-model="r.sourceModel" filterable clearable
              placeholder="源模型" size="mini"
              :disabled="!strategy.modelRoutingEnabled"
              style="flex: 1; max-width: 220px;"
            >
              <el-option v-for="m in sourceCandidates(i)" :key="m" :label="m" :value="m" />
            </el-select>
            <span class="arrow-sm">→</span>
            <el-select
              v-model="r.targetModel" filterable clearable
              placeholder="目标模型" size="mini"
              :disabled="!strategy.modelRoutingEnabled"
              style="flex: 1; max-width: 220px;"
            >
              <el-option v-for="m in availableModels" :key="m" :label="m" :value="m" />
            </el-select>
            <el-button size="mini" type="text" class="delete-btn" :disabled="!strategy.modelRoutingEnabled" @click="removeModelRoute(i)">删除</el-button>
          </div>
        </div>
        <div v-else class="panel-empty">暂无模型路由规则</div>
        <div class="panel-footer">
          <el-button size="mini" type="primary" plain icon="el-icon-plus"
            :disabled="!strategy.modelRoutingEnabled" @click="addModelRoute">添加规则</el-button>
          <el-button type="primary" size="mini" circle icon="el-icon-check"
            :loading="routingSaving" @click="saveModelRouting" title="保存模型路由" />
        </div>
      </div>

      <!-- 模型路由命中观察 -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">模型路由命中</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">今日</span>
          </div>
        </div>
        <div class="depth" v-if="routingStats.todayHits">
          <div class="depth-row">
            <b>命中次数</b>
            <div class="depth-bar"><span style="width:100%"></span></div>
            <strong>{{ routingStats.todayHits }}</strong>
          </div>
          <div class="depth-row">
            <b>规则数量</b>
            <div class="depth-bar"><span :style="{ width: routingStats.ruleCount ? '100%' : '0%' }"></span></div>
            <strong>{{ routingStats.ruleCount }}</strong>
          </div>
        </div>
        <div v-else class="panel-empty">今日暂无模型路由命中</div>
      </div>
    </section>

    <!-- 第三行：成本保护图表 24h -->
    <section class="grid two" style="margin-top: 0">
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">成本保护</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">24h</span>
          </div>
        </div>
        <div v-if="degradationStats.totalDegradedCalls > 0">
          <div class="cost-saved-tag" v-if="degradationStats.totalSavedCost > 0">
            <span class="tag green">已节省 ¥ {{ $formatNumber(degradationStats.totalSavedCost) }}</span>
          </div>
          <div ref="costChart" class="chart-container"></div>
          <div class="cost-meta" v-if="costMeta">
            <div class="value-row-sm"><small>累计节省</small><strong>{{ costMeta.saved }}</strong></div>
            <div class="value-row-sm"><small>降级次数</small><strong>{{ costMeta.count }}</strong></div>
            <div class="value-row-sm"><small>平均每次</small><strong>{{ costMeta.avgSave }}</strong></div>
          </div>
        </div>
        <div v-else class="panel-empty">暂无降级记录</div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <div class="panel-head-left">
            <h3 class="panel-title" style="font-size: 15px;">降级明细</h3>
            <span class="tag blue" style="font-size: 10px; height: 20px;">节省构成</span>
          </div>
        </div>
        <div class="detail-cards" v-if="degradationStats.details && degradationStats.details.length">
          <div class="detail-card" v-for="(d, i) in degradationStats.details" :key="i">
            <div class="detail-card__models">
              <span class="tag green">{{ d.modelName }}</span>
              <span class="arrow-sm">→</span>
              <span class="tag amber">{{ d.degradedTo }}</span>
            </div>
            <div class="detail-card__meta">
              <span>调用 <strong>{{ d.callCount }}</strong> 次</span>
              <span class="cost-amount">¥ {{ $formatNumber(d.savedCost) }}</span>
            </div>
          </div>
        </div>
        <div v-else class="panel-empty">暂无降级明细</div>
      </div>
    </section>

    <!-- 特殊账号对话框 -->
    <el-dialog :title="accountDialogTitle" :visible.sync="accountDialogVisible" width="480px" :close-on-click-modal="false">
      <el-form ref="accountForm" :model="accountForm" :rules="accountRules" label-width="100px" size="small">
        <el-form-item label="选择账号" prop="accountId">
          <el-select v-model="accountForm.accountId" filterable placeholder="选择账号" style="width: 100%" @change="onAccountSelected" popper-class="mobile-popper">
            <el-option v-for="a in filteredAccounts" :key="a.id"
              :label="a.name + ' (' + a.username + ')'" :value="a.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="账号名称" prop="accountName">
          <el-input v-model="accountForm.accountName" disabled />
        </el-form-item>
        <el-form-item label="每日上限" prop="dailyLimit">
          <el-input v-model.number="accountForm.dailyLimit" type="number" :min="1" :max="99999" style="width: 100%" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="accountDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="submitAccount" :loading="accountSubmitting">确定</el-button>
      </span>
    </el-dialog>
  </main>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'RouteStrategyPage',
  data() {
    return {
      strategy: {
        enabled: false,
        modelRoutingEnabled: false,
        defaultDailyLimit: 100,
        degradationTarget: '',
        models: [],
        accountOverrides: [],
        modelRoutes: []
      },
      degradationStats: {
        totalDegradedCalls: 0,
        totalSavedCost: 0,
        details: [],
        costTrend: []
      },
      hitStats: {
        cacheHits: 0,
        degradedCalls: 0,
        accountAccess: 0
      },
      routingStats: {
        todayHits: 0,
        ruleCount: 0,
        enabled: false
      },
      routingSaving: false,
      saving: false,
      availableModels: [],
      modelToAdd: '',
      availableAccounts: [],
      accountEditOriginalId: null,
      accountDialogVisible: false,
      accountDialogTitle: '',
      accountSubmitting: false,
      accountEditIndex: -1,
      accountPage: 1,
      accountPageSize: 2,
      accountSearchQuery: '',
      costChart: null,
      accountForm: {
        accountId: null,
        accountName: '',
        dailyLimit: 100
      },
      accountRules: {
        accountId: [
          { required: true, message: '请选择账号', trigger: 'change' }
        ],
        dailyLimit: [
          { required: true, type: 'number', min: 1, message: '请输入每日上限', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    strategyHits() {
      const raw = [
        { label: '缓存命中复用', count: this.hitStats.cacheHits || 0 },
        { label: '降级策略生效', count: this.hitStats.degradedCalls || 0 },
        { label: '特殊账号访问', count: this.hitStats.accountAccess || 0 }
      ]
      const max = Math.max(...raw.map(x => x.count), 1)
      return raw.filter(x => x.count > 0).map(x => ({
        label: x.label,
        count: x.count + ' 次',
        pct: Math.round(x.count / max * 100)
      }))
    },
    costMeta() {
      const d = this.degradationStats
      if (!d.totalDegradedCalls) return null
      const avg = d.totalDegradedCalls > 0 ? d.totalSavedCost / d.totalDegradedCalls : 0
      return {
        saved: '¥' + this.$formatNumber(d.totalSavedCost),
        count: d.totalDegradedCalls + ' 次',
        avgSave: '¥' + this.$formatNumber(avg)
      }
    },
    filteredAccounts() {
      const ids = new Set(this.strategy.accountOverrides.map(o => o.accountId))
      if (this.accountEditOriginalId != null) ids.delete(this.accountEditOriginalId)
      return this.availableAccounts.filter(a => !ids.has(a.id))
    },
    filteredOverrides() {
      if (!this.accountSearchQuery) return this.strategy.accountOverrides
      const q = this.accountSearchQuery.toLowerCase()
      return this.strategy.accountOverrides.filter(a =>
        a.accountName && a.accountName.toLowerCase().includes(q)
      )
    },
    pagedOverrides() {
      const start = (this.accountPage - 1) * this.accountPageSize
      return this.filteredOverrides.slice(start, start + this.accountPageSize)
    }
  },
  mounted() {
    this.fetchStrategy()
    this.fetchDegradationStats()
    this.fetchHitStats()
    this.fetchRoutingStats()
    this.fetchAvailableModels()
    this.fetchAvailableAccounts()
  },
  beforeDestroy() {
    if (this.costChart) this.costChart.dispose()
  },
  methods: {
    async fetchStrategy() {
      try {
        const res = await this.$http.get('/admin/route-strategy')
        this.strategy = {
          enabled: res.enabled !== undefined ? res.enabled : false,
          modelRoutingEnabled: res.modelRoutingEnabled !== undefined ? res.modelRoutingEnabled : false,
          defaultDailyLimit: res.defaultDailyLimit || 100,
          degradationTarget: res.degradationTarget || '',
          models: res.models || [],
          accountOverrides: (res.accountOverrides || []).map(a => ({
            accountId: a.accountId,
            accountName: a.accountName || '',
            dailyLimit: a.dailyLimit
          })),
          modelRoutes: (res.modelRoutes || []).map(r => ({
            sourceModel: r.sourceModel,
            targetModel: r.targetModel
          }))
        }
      } catch {
        this.$message.error('加载路由策略失败')
      }
    },
    async fetchDegradationStats() {
      try {
        const res = await this.$http.get('/admin/route-strategy/degradation-stats')
        this.degradationStats = {
          totalDegradedCalls: res.totalDegradedCalls || 0,
          totalSavedCost: res.totalSavedCost || 0,
          details: res.details || [],
          costTrend: res.costTrend || []
        }
        this.$nextTick(() => this.renderCostChart())
      } catch {
        this.$message.error('加载省钱统计失败')
      }
    },
    async fetchHitStats() {
      try {
        this.hitStats = await this.$http.get('/admin/route-strategy/hit-stats')
      } catch {
        this.hitStats = { cacheHits: 0, degradedCalls: 0, accountAccess: 0 }
      }
    },
    async saveStrategy() {
      this.saving = true
      try {
        await this.$http.post('/admin/route-strategy/limits', {
          defaultDailyLimit: this.strategy.defaultDailyLimit,
          degradationTarget: this.strategy.degradationTarget || undefined,
          models: this.strategy.models
        })
        this.$message.success('保存成功')
      } catch {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async toggleEnabled(val) {
      try {
        await this.$http.post('/admin/route-strategy/toggle', { enabled: val })
      } catch {
        this.$message.error('切换失败')
        this.strategy.enabled = !val
      }
    },
    async addAccountRemote(data) {
      try {
        await this.$http.post('/admin/route-strategy/accounts', data)
        this.$message.success('添加成功')
      } catch {
        this.$message.error('添加失败')
        throw new Error()
      }
    },
    async removeAccountRemote(accountId) {
      try {
        await this.$http.delete('/admin/route-strategy/accounts/' + accountId)
      } catch {
        this.$message.error('删除失败')
        throw new Error()
      }
    },
    async fetchAvailableModels() {
      try {
        this.availableModels = await this.$http.get('/admin/route-strategy/available-models')
      } catch {
        this.availableModels = []
      }
    },
    sourceCandidates(i) {
      const current = this.strategy.modelRoutes[i] ? this.strategy.modelRoutes[i].sourceModel : ''
      const used = new Set(this.strategy.modelRoutes.map(r => r.sourceModel).filter(Boolean))
      used.delete(current)
      return this.availableModels.filter(m => !used.has(m))
    },
    async fetchAvailableAccounts() {
      try {
        this.availableAccounts = await this.$http.get('/admin/route-strategy/available-accounts')
      } catch {
        this.availableAccounts = []
      }
    },
    onAccountSelected(val) {
      const account = this.availableAccounts.find(a => a.id === val)
      if (account) this.accountForm.accountName = account.name || account.username
    },
    onModelSelected(val) {
      if (val && !this.strategy.models.includes(val)) this.strategy.models.push(val)
      this.modelToAdd = ''
    },
    removeModel(index) { this.strategy.models.splice(index, 1) },
    async fetchRoutingStats() {
      try {
        this.routingStats = await this.$http.get('/admin/route-strategy/model-routing-stats')
      } catch {
        this.routingStats = { todayHits: 0, ruleCount: 0, enabled: false }
      }
    },
    addModelRoute() {
      this.strategy.modelRoutes.push({ sourceModel: '', targetModel: '' })
    },
    removeModelRoute(index) {
      this.strategy.modelRoutes.splice(index, 1)
    },
    async toggleModelRouting(val) {
      try {
        const rules = this.strategy.modelRoutes
          .filter(r => r.sourceModel && r.targetModel)
          .map(r => ({ sourceModel: r.sourceModel, targetModel: r.targetModel }))
        await this.$http.put('/admin/route-strategy/model-routing', {
          enabled: val,
          rules
        })
        this.strategy.modelRoutes = rules
        this.$message.success('模型路由开关已更新')
      } catch {
        this.$message.error('更新失败')
        this.strategy.modelRoutingEnabled = !val
      }
    },
    async saveModelRouting() {
      this.routingSaving = true
      try {
        const rules = this.strategy.modelRoutes
          .filter(r => r.sourceModel && r.targetModel)
          .map(r => ({ sourceModel: r.sourceModel, targetModel: r.targetModel }))
        await this.$http.put('/admin/route-strategy/model-routing', {
          enabled: this.strategy.modelRoutingEnabled,
          rules
        })
        this.strategy.modelRoutes = rules
        this.$message.success('模型路由已保存')
        this.fetchRoutingStats()
      } catch {
        this.$message.error('保存失败，请检查源/目标模型是否在可用范围内')
      } finally {
        this.routingSaving = false
      }
    },
    onAddAccount() {
      this.accountDialogTitle = '添加特殊账号'
      this.accountEditIndex = -1
      this.accountEditOriginalId = null
      this.accountForm = { accountId: null, accountName: '', dailyLimit: 100 }
      this.accountDialogVisible = true
      this.$nextTick(() => this.$refs.accountForm?.clearValidate())
    },
    onEditAccount(row) {
      this.accountDialogTitle = '编辑特殊账号'
      const idx = this.strategy.accountOverrides.indexOf(row)
      this.accountEditIndex = idx
      this.accountEditOriginalId = row.accountId
      this.accountForm = { ...row }
      this.accountDialogVisible = true
      this.$nextTick(() => this.$refs.accountForm?.clearValidate())
    },
    submitAccount() {
      this.$refs.accountForm.validate(async valid => {
        if (!valid) return
        const data = {
          accountId: this.accountForm.accountId,
          accountName: this.accountForm.accountName || undefined,
          dailyLimit: this.accountForm.dailyLimit
        }
        try {
          if (this.accountEditIndex >= 0) {
            // 编辑：先删旧记录再添加
            const old = this.strategy.accountOverrides[this.accountEditIndex]
            await this.removeAccountRemote(old.accountId)
            await this.addAccountRemote(data)
            this.strategy.accountOverrides.splice(this.accountEditIndex, 1, data)
          } else {
            await this.addAccountRemote(data)
            this.strategy.accountOverrides.push(data)
          }
          this.accountDialogVisible = false
        } catch {
          // error already handled in addAccountRemote/removeAccountRemote
        }
      })
    },
    onDeleteAccount(index) {
      this.$confirm('确定删除该特殊账号额度？', '确认删除', {
        confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
      }).then(async () => {
        const item = this.pagedOverrides[index]
        if (!item) return
        try {
          await this.removeAccountRemote(item.accountId)
          const realIndex = this.strategy.accountOverrides.findIndex(a => a.accountId === item.accountId)
          if (realIndex >= 0) this.strategy.accountOverrides.splice(realIndex, 1)
          this.$message.success('已移除')
        } catch {
          // error already handled
        }
      }).catch(() => {})
    },
    onAccountSearch() {
      this.accountPage = 1
    },
    renderCostChart() {
      const el = this.$refs.costChart
      if (!el) return
      if (this.costChart) this.costChart.dispose()
      const trend = this.degradationStats.costTrend || []
      if (!trend.length) { el.innerHTML = ''; return }

      // match chart height to sibling detail panel
      const sections = this.$el.querySelectorAll('.grid.two')
      const lastSection = sections.length > 1 ? sections[sections.length - 1] : null
      if (lastSection && lastSection.children.length > 1) {
        const h = lastSection.children[1].offsetHeight
        if (h > 50) el.style.height = (h - 48) + 'px'
      }

      this.costChart = echarts.init(el)
      const hours = trend.map(t => {
        const d = new Date(t.hour)
        return d.getHours() + ':00'
      })
      const values = trend.map(t => Number(t.savedCost || 0))

      // resize after render to fit container
      this.costChart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#1a2638',
          borderColor: 'rgba(151,171,207,0.18)',
          textStyle: { color: '#e7eef8' },
          formatter: p => {
            const v = p[0].value
            return p[0].axisValue + '<br/>节省 ¥ ' + v.toFixed(2)
          }
        },
        grid: { left: 40, right: 16, top: 12, bottom: 24 },
        xAxis: {
          type: 'category',
          data: hours,
          axisLabel: { fontSize: 10, color: '#71839b' },
          axisLine: { lineStyle: { color: 'rgba(151,171,207,0.18)' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(151,171,207,0.08)' } },
          axisLabel: { fontSize: 10, color: '#71839b', formatter: '¥{value}' }
        },
        series: [{
          type: 'line',
          data: values,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#34c98f', width: 2 },
          itemStyle: { color: '#34c98f' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52,201,143,0.35)' },
              { offset: 1, color: 'rgba(52,201,143,0.02)' }
            ])
          }
        }]
      })
      this.costChart.resize()
    },
      }
}
</script>

<style scoped>
.route-strategy-page {
  display: grid;
  gap: 16px;
  margin: 0 24px;
}

.strategy-alert {
  margin-bottom: 0;
}

/* ===== 复用 Dashboard 的 panel 体系 ===== */
.panel {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.panel-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 880;
  color: var(--text);
}
.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.panel-empty {
  text-align: center;
  padding: 32px 0;
  color: var(--muted);
  font-size: 13px;
}

/* ===== Grid ===== */
.grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}
.grid.two {
  grid-template-columns: 1.2fr 0.8fr;
}
.grid.two > * {
  min-width: 0;
}

/* ===== 路由流向图 ===== */
.router-map {
  display: grid;
  grid-template-columns: 1fr 48px 1fr 48px 1fr;
  align-items: stretch;
  gap: 6px;
  margin-bottom: 18px;
}
.route-box {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(133,164,255,.16);
  background: rgba(255,255,255,.045);
}
.route-box h4 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 850;
  color: var(--text);
}
.route-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  margin-top: 6px;
  border-radius: 6px;
  background: rgba(0,0,0,.18);
  color: #bdcde3;
  font-size: 12px;
}
.route-item:first-of-type {
  margin-top: 0;
}
.route-item b {
  font-size: 12px;
  white-space: nowrap;
}
.route-item .tag {
  font-size: 10px;
  height: 20px;
  padding: 0 6px;
}
.arrow {
  display: grid;
  place-items: center;
  color: var(--cyan);
  font-size: 24px;
  font-weight: 200;
}
.muted-text {
  color: var(--muted-2);
}

/* ===== 策略表单 ===== */
.strategy-form :deep(.el-form-item) {
  margin-bottom: 14px;
}
.strategy-form :deep(.el-form-item__content) {
  width: 340px;
}
.strategy-form :deep(.el-form-item__content .el-input),
.strategy-form :deep(.el-form-item__content .el-select) {
  width: 100%;
}
.form-help {
  margin-left: 10px;
  font-size: 12px;
  color: rgba(255,255,255,.35);
}
.model-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  width: 100%;
}
.model-tag-list :deep(.el-select) {
  width: 100%;
}
.panel-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid var(--line-soft);
}

/* ===== 深色覆盖：select 悬停 / input-number 边框 ===== */
.route-strategy-page :deep(.el-select-dropdown__item.hover),
.route-strategy-page :deep(.el-select-dropdown__item:hover) {
  background: rgba(50, 200, 245, 0.15);
  color: #fff;
}

.route-strategy-page :deep(.el-input-number__decrease),
.route-strategy-page :deep(.el-input-number__increase) {
  background: #1f2a3c;
  border-color: rgba(151, 171, 207, 0.18);
  color: #aabdd4;
}
.route-strategy-page :deep(.el-input-number__decrease:hover),
.route-strategy-page :deep(.el-input-number__increase:hover) {
  background: #2a3650;
  color: var(--cyan);
}
.route-strategy-page :deep(.el-input-number .el-input__inner) {
  border-color: rgba(151, 171, 207, 0.18);
}

/* ===== 命中盘口（复用 Dashboard depth 样式） ===== */
.depth {
  display: grid;
  gap: 12px;
}
.depth-row {
  display: grid;
  grid-template-columns: 110px 1fr 90px;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--text);
}
.depth-row b {
  font-weight: 800;
  color: #c6d4e9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.depth-bar {
  height: 10px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.depth-bar span {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, var(--cyan), var(--blue));
  box-shadow: 0 0 12px rgba(50, 200, 245, 0.3);
  transition: width 0.4s ease;
}
.depth-row strong {
  text-align: right;
  font-weight: 850;
  color: var(--text);
}

/* ===== 成本保护 ===== */
.cost-saved-tag {
  margin-bottom: 10px;
}
.chart-container {
  width: 100%;
  height: 140px;
}
.cost-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
}
.value-row-sm {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(133, 164, 255, 0.12);
  background: rgba(255, 255, 255, 0.045);
  text-align: center;
}
.value-row-sm small {
  color: var(--muted);
  font-size: 11px;
}
.value-row-sm strong {
  color: var(--text);
  font-size: 14px;
  font-weight: 900;
}

/* ===== 特殊账号 ===== */
.cost-amount {
  color: #34c98f;
  font-weight: 850;
}

/* ===== 模型路由规则 ===== */
.route-rule-list {
  display: grid;
  gap: 8px;
  margin-top: 8px;
}
.route-rule-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(133, 164, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}
.route-rule-row :deep(.el-select) {
  flex: 1;
}

/* ===== 降级明细卡片 ===== */
.detail-cards {
  display: grid;
  gap: 8px;
}
.detail-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(133, 164, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}
.detail-card__models {
  display: flex;
  align-items: center;
  gap: 6px;
}
.detail-card__models .tag {
  font-size: 11px;
  height: 22px;
  padding: 0 8px;
}
.arrow-sm {
  color: var(--muted);
  font-size: 14px;
}
.detail-card__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: var(--muted);
}
.detail-card__meta strong {
  color: var(--text);
}
.detail-card__meta .cost-amount {
  font-size: 13px;
}
.delete-btn {
  color: #e74c3c;
}

.account-pagination {
  margin-top: 8px;
  text-align: right;
}

.account-search-input {
  width: 160px;
}
.panel-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ===== 特殊账号移动端卡片（760px 以下替代 el-table） ===== */
.account-cards {
  display: none;
}
.account-card {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(133, 164, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  margin-bottom: 8px;
}
.account-card__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
}
.account-card__row .label {
  color: var(--muted-2);
  font-weight: 850;
  font-size: 11px;
}
.account-card__row .el-button--text {
  font-size: 12px;
  padding: 0 4px;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .route-strategy-page {
    margin: 0 16px;
  }
  .grid.two {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 760px) {
  .route-strategy-page {
    margin: 0 8px;
  }
  .panel {
    padding: 12px;
  }
  .panel-title {
    font-size: 14px !important;
  }
  .strategy-form :deep(.el-form-item__content) {
    width: 100% !important;
  }
  .strategy-form :deep(.el-form-item__label) {
    float: none;
    display: block;
    text-align: left;
    padding: 0 0 4px;
    width: auto !important;
  }
  .strategy-form :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
  .strategy-form :deep(.el-select-dropdown) {
    min-width: auto !important;
    width: auto !important;
    max-width: calc(100vw - 32px) !important;
  }
  /* 手机端只读：禁止所有交互 */
  .route-strategy-page .el-button,
  .route-strategy-page .el-switch,
  .route-strategy-page .el-input,
  .route-strategy-page .el-select {
    pointer-events: none !important;
  }
  .route-strategy-page .panel-footer { display: none !important; }
  .route-strategy-page .el-dialog { display: none !important; }
  .router-map {
    grid-template-columns: 1fr !important;
    gap: 4px !important;
  }
  .arrow {
    transform: rotate(90deg);
    font-size: 18px !important;
    padding: 2px 0;
  }
  .route-box {
    padding: 10px;
  }
  .route-box h4 {
    margin-bottom: 6px;
    font-size: 12px;
  }
  .route-item {
    padding: 5px 8px;
    font-size: 11px;
  }
  .depth-row {
    grid-template-columns: 80px 1fr 60px !important;
    gap: 8px !important;
    font-size: 12px !important;
  }
  .cost-meta {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
  .value-row-sm {
    padding: 6px;
  }
  .value-row-sm strong {
    font-size: 12px;
  }
  .detail-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .detail-card__meta {
    width: 100%;
    justify-content: space-between;
  }
  .model-tag-list :deep(.el-select) {
    width: 100%;
  }
  .account-table { display: none !important; }
  .account-cards { display: block; }
  .account-pagination { display: none !important; }
  .strategy-form :deep(.el-form-item__content .el-input),
  .strategy-form :deep(.el-form-item__content .el-select) {
    max-width: 100%;
  }
  .chart-container {
    height: 140px;
  }
}
@media (max-width: 420px) {
  .panel-head {
    flex-wrap: wrap;
    gap: 8px;
  }
  .panel-head .el-button--mini {
    padding: 6px 10px;
    font-size: 11px;
  }
  .depth-row {
    grid-template-columns: 60px 1fr 50px !important;
    gap: 6px !important;
    font-size: 11px !important;
  }
  .cost-meta {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .value-row-sm {
    flex-direction: row;
    justify-content: space-between;
    padding: 6px 10px;
  }
}
</style>
